import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getOrder, createReturn } from '../services/api';
import toast from 'react-hot-toast';

const reasons = [
  'Wrong size received',
  'Damaged or defective product',
  'Not as described',
  'Wrong item received',
  'Changed my mind',
  'Other',
];

export default function ReturnPage() {
  const { orderId } = useParams();
  const navigate = useNavigate();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  const [selectedItems, setSelectedItems] = useState([]);
  const [reason, setReason] = useState('');
  const [description, setDescription] = useState('');
  const [refundMethod, setRefundMethod] = useState('bkash');

  useEffect(() => {
    getOrder(orderId).then(res => setOrder(res.data)).finally(() => setLoading(false));
  }, [orderId]);

  const toggleItem = (item) => {
    setSelectedItems(prev =>
      prev.find(i => i.name === item.name && i.size === item.size)
        ? prev.filter(i => !(i.name === item.name && i.size === item.size))
        : [...prev, item]
    );
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (selectedItems.length === 0) return toast.error('Please select at least one item to return');
    if (!reason) return toast.error('Please select a reason');
    setSubmitting(true);
    try {
      await createReturn({
        order: orderId,
        items: selectedItems.map(i => ({
          product: i.product, name: i.name, size: i.size,
          quantity: i.quantity, price: i.price,
        })),
        reason, description, refundMethod,
      });
      toast.success('Return request submitted! We will review it within 24 hours.');
      navigate(`/orders/${orderId}`);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to submit return');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return <div className="max-w-2xl mx-auto px-4 py-10"><div className="skeleton h-80 rounded-2xl" /></div>;
  if (!order) return null;

  return (
    <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
      <h1 className="font-display text-3xl font-bold text-yamass-black mb-2">Request a Return</h1>
      <p className="text-gray-400 text-sm mb-8">Order #{order._id.slice(-8).toUpperCase()}</p>

      <form onSubmit={handleSubmit} className="space-y-6">

        {/* Step 1: Select items */}
        <div className="bg-yamass-cream rounded-2xl p-6">
          <h2 className="font-semibold text-yamass-black mb-4">1. Select items to return</h2>
          <div className="space-y-3">
            {order.orderItems.map((item, i) => {
              const selected = selectedItems.find(s => s.name === item.name && s.size === item.size);
              return (
                <button
                  type="button"
                  key={i}
                  onClick={() => toggleItem(item)}
                  className={`w-full flex items-center gap-4 p-3 rounded-xl border-2 text-left transition-all ${
                    selected ? 'border-yamass-black bg-white' : 'border-transparent bg-white/50 hover:bg-white'
                  }`}
                >
                  <div className={`w-5 h-5 rounded border-2 flex items-center justify-center flex-shrink-0 transition-colors ${
                    selected ? 'border-yamass-black bg-yamass-black' : 'border-gray-300'
                  }`}>
                    {selected && <span className="text-white text-xs">✓</span>}
                  </div>
                  <div className="w-10 h-10 rounded-lg overflow-hidden bg-yamass-cream flex-shrink-0">
                    {item.image ? <img src={item.image} alt="" className="w-full h-full object-cover" /> : <div className="w-full h-full flex items-center justify-center text-lg">👕</div>}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-yamass-black truncate">{item.name}</p>
                    <p className="text-xs text-gray-400">Size: {item.size} · Qty: {item.quantity}</p>
                  </div>
                  <p className="text-sm font-semibold text-yamass-black">৳{(item.price * item.quantity).toLocaleString()}</p>
                </button>
              );
            })}
          </div>
        </div>

        {/* Step 2: Reason */}
        <div className="bg-yamass-cream rounded-2xl p-6">
          <h2 className="font-semibold text-yamass-black mb-4">2. Reason for return</h2>
          <div className="grid grid-cols-2 gap-2 mb-4">
            {reasons.map(r => (
              <button
                type="button"
                key={r}
                onClick={() => setReason(r)}
                className={`text-left text-sm px-4 py-3 rounded-xl border-2 transition-all ${
                  reason === r ? 'border-yamass-black bg-white font-medium' : 'border-transparent bg-white/50 hover:bg-white text-gray-600'
                }`}
              >
                {r}
              </button>
            ))}
          </div>
          <textarea
            value={description}
            onChange={e => setDescription(e.target.value)}
            placeholder="Additional details (optional)..."
            rows={3}
            className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-yamass-accent resize-none bg-white"
          />
        </div>

        {/* Step 3: Refund method */}
        <div className="bg-yamass-cream rounded-2xl p-6">
          <h2 className="font-semibold text-yamass-black mb-4">3. Refund method</h2>
          <div className="flex gap-3">
            {[
              { value: 'bkash',    label: 'bKash',    icon: '📱' },
              { value: 'nagad',    label: 'Nagad',    icon: '💳' },
              { value: 'original', label: 'Original', icon: '🔄' },
            ].map(m => (
              <button
                type="button"
                key={m.value}
                onClick={() => setRefundMethod(m.value)}
                className={`flex-1 flex flex-col items-center gap-1 py-3 rounded-xl border-2 text-sm transition-all ${
                  refundMethod === m.value ? 'border-yamass-black bg-white font-medium' : 'border-transparent bg-white/50 hover:bg-white text-gray-500'
                }`}
              >
                <span className="text-xl">{m.icon}</span>
                {m.label}
              </button>
            ))}
          </div>
        </div>

        <button
          type="submit"
          disabled={submitting}
          className="w-full bg-yamass-black text-white py-4 rounded-full text-sm font-semibold hover:bg-yamass-accent transition-colors disabled:opacity-50"
        >
          {submitting ? 'Submitting...' : 'Submit Return Request'}
        </button>
      </form>
    </div>
  );
}
